import { Link, useLocation } from 'react-router-dom';
import { mainSiteUrl } from '@/data/categories';
import { allSpreadsheetPages, type SpreadsheetPageData } from '@/data/spreadsheet-pages';

const SLUG_MAP: Record<string, string> = {
  'taobao-spreadsheet': 'taobao-spreadsheet',
  'weidian-spreadsheet': 'weidian-spreadsheet',
  '1688-spreadsheet': '1688-spreadsheet',
  'yoybuy-spreadsheet': 'yoybuy-spreadsheet',
  'kakobuy-spreadsheet': 'kakobuy-spreadsheet',
  'superbuy-spreadsheet': 'superbuy-spreadsheet',
  'pandabuy-spreadsheet': 'pandabuy-spreadsheet',
  'sugargoo-spreadsheet': 'sugargoo-spreadsheet',
  'mulebuy-spreadsheet': 'mulebuy-spreadsheet',
};

function getPageData(slug: string): SpreadsheetPageData | null {
  const key = SLUG_MAP[slug];
  if (!key) return null;
  return allSpreadsheetPages[key] || null;
}

export default function SpreadsheetLanding() {
  const location = useLocation();
  // извлекаем последний сегмент пути как slug, совместимо с __BASE_PATH__
  const segments = location.pathname.split('/').filter(Boolean);
  const slug = segments[segments.length - 1] || '';
  const data = getPageData(slug);

  const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://yoybuy.life';
  const pageUrl = `${SITE_URL}/${slug}`;

  if (!data) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-background-50 px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 mx-auto mb-6">
            <i className="ri-error-warning-line text-2xl"></i>
          </div>
          <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
            Страница не найдена
          </h1>
          <p className="text-sm text-foreground-600 mb-6">
            Запрашиваемая страница не существует или была перемещена. Проверьте URL или вернитесь на главную.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/"
              className="whitespace-nowrap px-6 py-3 text-sm font-semibold rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-home-line mr-2"></i>
              На главную
            </Link>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap px-6 py-3 text-sm font-semibold rounded-full border-2 border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
            >
              Yoybuy Sheet
            </a>
          </div>
        </div>
      </main>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: data.pageTitle,
    description: data.metaDescription,
    author: {
      '@type': 'Organization',
      name: 'Yoybuy Spreadsheet',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Yoybuy Spreadsheet',
      url: SITE_URL,
    },
    datePublished: '2026-07-05',
    dateModified: '2026-07-05',
    inLanguage: 'ru',
    url: pageUrl,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': pageUrl,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Главная',
        item: SITE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: data.platformName + ' Spreadsheet',
        item: pageUrl,
      },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': pageUrl,
    },
    mainEntity: data.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <main>
        <nav className="sticky top-0 z-50 bg-background-50/80 backdrop-blur-md border-b border-background-200/70">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between h-14">
              <Link to="/" className="flex items-center gap-2 group">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
                  <i className="ri-table-line text-sm"></i>
                </div>
                <span className="font-heading text-base font-semibold text-foreground-900 whitespace-nowrap">
                  Yoybuy Spreadsheet
                </span>
              </Link>
              <div className="flex items-center gap-3">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="whitespace-nowrap px-4 py-2 text-sm font-medium rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer hidden sm:inline-flex"
                >
                  Yoybuy Sheet
                </a>
              </div>
            </div>
          </div>
        </nav>

        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-500 transition-colors">Главная</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700 font-medium">{data.platformName} Spreadsheet</span>
          </nav>

          <article>
            <header className="mb-10">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="px-3 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-700">
                  Spreadsheet
                </span>
                <span className="text-xs text-foreground-500">Обновлено: 05.07.2026</span>
                <span className="text-xs text-foreground-400 flex items-center gap-1">
                  <i className="ri-time-line"></i> 10 мин чтения
                </span>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                {data.heroTitle}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {data.heroSubtitle}
              </p>
            </header>

            <div className="prose-custom space-y-6">
              <section>
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                  Что такое {data.platformName} Spreadsheet и зачем он нужен
                </h2>
                {data.intro.map((p, i) => (
                  <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    {p}
                  </p>
                ))}
              </section>

              <div className="bg-background-100 rounded-2xl p-6 md:p-8 my-8">
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">
                  Ключевые преимущества {data.platformName} Spreadsheet
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {data.benefits.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-md bg-accent-100 text-accent-600 mt-0.5">
                        <i className={`${item.icon} text-sm`}></i>
                      </span>
                      <div>
                        <span className="text-sm font-semibold text-foreground-800">{item.title}</span>
                        <p className="text-sm text-foreground-600">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                  Как использовать {data.platformName} Spreadsheet: пошаговая инструкция
                </h2>
                <div className="space-y-5">
                  {data.howItWorks.map((step) => (
                    <div key={step.step} className="flex gap-4">
                      <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-bold">
                        {step.step}
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-foreground-800 mb-2">{step.title}</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mt-10">
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                  {data.comparisonTitle}
                </h2>
                <div className="overflow-x-auto rounded-xl border border-background-200/70">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-background-100">
                        {data.comparisonHeaders.map((h, i) => (
                          <th key={i} className={`px-4 py-3 text-left font-semibold text-foreground-800 whitespace-nowrap ${i === 0 ? '' : ''}`}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.comparisonRows.map((row, ri) => (
                        <tr key={ri} className="border-t border-background-200/70 even:bg-background-50">
                          <td className="px-4 py-3 text-foreground-700 font-medium whitespace-nowrap">{row.feature}</td>
                          {row.values.map((val, vi) => (
                            <td key={vi} className={`px-4 py-3 text-foreground-600 ${vi === 0 ? 'bg-accent-50/50' : ''}`}>
                              {val}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="mt-10">
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                  Особенности {data.platformName} Spreadsheet
                </h2>
                <div className="space-y-4">
                  {data.highlights.map((h, i) => (
                    <div key={i} className="p-4 rounded-xl bg-accent-50 border border-accent-200/50">
                      <h3 className="text-sm font-semibold text-foreground-800 mb-1">
                        <i className="ri-star-line mr-1 text-accent-500"></i>
                        {h.title}
                      </h3>
                      <p className="text-sm text-foreground-600">{h.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mt-10">
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                  Часто задаваемые вопросы о {data.platformName} Spreadsheet
                </h2>
                <div className="space-y-4">
                  {data.faqs.map((faq, i) => (
                    <div key={i}>
                      <h3 className="text-base font-semibold text-foreground-800 mb-2">{faq.q}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            <div className="mt-12 p-6 md:p-8 rounded-2xl bg-background-100 border border-background-200/70 text-center">
              <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-3">
                {data.ctaTitle}
              </h3>
              <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
                {data.ctaDesc}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="whitespace-nowrap w-full sm:w-auto px-6 py-3 text-sm font-semibold rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-shopping-bag-3-line mr-2"></i>
                  Yoybuy Sheet
                </a>
                <Link
                  to="/"
                  className="whitespace-nowrap w-full sm:w-auto px-6 py-3 text-sm font-semibold rounded-full border-2 border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer text-center"
                >
                  <i className="ri-home-line mr-2"></i>
                  На главную
                </Link>
              </div>
            </div>
          </article>
        </div>

        <footer className="bg-accent-500 mt-16">
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-10">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-background-50 text-accent-500">
                  <i className="ri-table-line text-sm"></i>
                </div>
                <span className="font-heading text-base font-semibold text-background-50 whitespace-nowrap">
                  Yoybuy Spreadsheet
                </span>
              </Link>
              <div className="flex items-center gap-4 text-xs text-background-50/60 flex-wrap justify-center">
                <Link to="/" className="hover:text-background-50 transition-colors">Главная</Link>
                <Link to="/yoybuy-spreadsheet-guide" className="hover:text-background-50 transition-colors">Руководство</Link>
                <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="hover:text-background-50 transition-colors cursor-pointer">Основной сайт</a>
              </div>
              <p className="text-xs text-background-50/60">
                &copy; 2026 Yoybuy Spreadsheet. Все права защищены.
              </p>
            </div>
          </div>
        </footer>
      </main>
    </>
  );
}